import React, {useState, useMemo} from "react";
import {BrowserRouter as Router, Routes, Route, Navigate, useLocation} from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Home from "./components/Home";
import MemberPage from "./components/MemberPage";
import Login from "./components/Login";
import Dashboard from "./components/dashboard";
import Logout from "./components/Logout";
import Profile from "./components/Profile";
import ChangePassword from "./components/ChangePassword";
import MuscleGroups from "./components/MuscleGroups";
import Exercises from "./components/Exercises";
import Programs from "./components/Program";
import MemberProgram from "./components/MemberProgram";
import AdminChat from "./components/AdminChat";
import MemberChatArabic from "./components/MemberChat";
import HomePageNavBar from "./components/HomePageNavBar";
import AboutUs from "./components/AboutUs";
import { jwtDecode } from "jwt-decode";
import {Toast, ToastContainer} from "react-bootstrap";
import {FaCheckCircle, FaExclamationCircle} from "react-icons/fa";
import "./App.css";

export const ToastContext = React.createContext();
export const AuthContext = React.createContext();

// 🔹 استخراج userId من الـ token
const getUserIdFromToken = (token) => {
    if (!token) return null;
    try {
        const decoded = jwtDecode(token);
        return decoded.user_id || decoded.id;
    } catch (e) {
        console.error("Invalid token", e);
        return null;
    }
};

// ======================= APP =======================
function App() {
    const [token, setToken] = useState(localStorage.getItem("FORCE_GYM_TOKEN"));
    const [role, setRole] = useState(localStorage.getItem("FORCE_GYM_ROLE"));
    const [userId, setUserId] = useState(() => getUserIdFromToken(token));

    const [unreadCount, setUnreadCount] = useState(0);
    const [isChatOpen, setIsChatOpen] = useState(false);

    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("success");
    const [showToast, setShowToast] = useState(false);
    // 🔹 منع إعادة بناء كل Context في كل ريندر
    const authValue = useMemo(() => ({
        token,
        role,
        login: (token, role) => {
            localStorage.setItem("FORCE_GYM_TOKEN", token);
            localStorage.setItem("FORCE_GYM_ROLE", role);
            setToken(token);
            setRole(role);
            setUserId(getUserIdFromToken(token));
        },
        logout: () => {
            localStorage.removeItem("FORCE_GYM_TOKEN");
            localStorage.removeItem("FORCE_GYM_ROLE");
            setToken(null);
            setRole(null);
            setUserId(null);
        },
        userId
    }), [token, role, userId]);


    const showAppToast = (message, type = "success") => { setToastMessage(message); setToastType(type); setShowToast(true); };

    return (
        <ToastContext.Provider value={showAppToast}>
            <AuthContext.Provider value={authValue}>
                <Router>
                    <AppContent token={token} role={role}/>
                </Router>

                {/* 🔹 زر الشات يظهر فقط للمستخدم */}
                {token && role === "user" && (
                    <button
                        className="btn btn-primary position-fixed bottom-0 m-3 rounded-circle"
                        style={{width: "60px", height: "60px", zIndex: 1050}}
                        onClick={() => {
                            setIsChatOpen(!isChatOpen);
                            setUnreadCount(0);
                        }}
                    >
                        <i className="bi bi-send-fill fs-4"></i>
                        {unreadCount > 0 && (
                            <span
                                className="position-absolute top-0 translate-middle badge rounded-pill bg-danger"
                                style={{fontSize: "0.8rem"}}
                            >
                                {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                        )}
                    </button>
                )}

                {/* 🔹 الشات مفصول عن الراوتر حتى ما يعيد تحميل البيانات */}
                <MemberChatArabic
                    isOpen={isChatOpen}
                    setIsOpen={setIsChatOpen}
                    unreadCount={unreadCount}
                    setUnreadCount={setUnreadCount}
                />

                <ToastContainer position="top-end" className="p-3 custom-toast-container">
                    <Toast
                        className={`custom-toast toast-${toastType}`}
                        onClose={() => setShowToast(false)}
                        show={showToast}
                        delay={3000}
                        autohide
                    >
                        <Toast.Body className="text-white d-flex align-items-center gap-2 justify-content-between">
                            {toastType === "success" && <FaCheckCircle className="toast-icon" />}
                            {toastType === "danger" && <FaExclamationCircle className="toast-icon" />}

                            <span>{toastMessage}</span>
                        </Toast.Body>
                    </Toast>
                </ToastContainer>
            </AuthContext.Provider>
        </ToastContext.Provider>
    );
}

// ======================= APP CONTENT =======================
// 🔹 React.memo يمنع إعادة الريندر إلا إذا تغيّر token أو role
const AppContent = React.memo(function AppContent({token, role}) {
    const location = useLocation();

    const PrivateRoute = ({children, allowRoles}) => {
        if (!token) return <Navigate to="/login" replace />;
        if (allowRoles && !allowRoles.includes(role)) {
            return <Navigate to="/home" replace />;
        }
        return children;
    };

    const needSidebar = (pathname, role) => {
        if (!token) return false;
        if (role === "admin") {
            return ["/dashboard", "/members", "/muscle", "/muscle_exercises", "/programs", "/members-programs", "/admin-chat"].includes(pathname);
        }
        return false;
    };

    const showNavbar = role !== "admin";

    return (
        <div className="d-flex">
            {token && needSidebar(location.pathname, role) && <Sidebar role={role} />}
            <div className="flex-grow-1">
                {showNavbar && <HomePageNavBar />}

                <Routes>
                    <Route path="/aboutUs" element={<AboutUs />} />

                    <Route
                        path="/login"
                        element={
                            token ? (
                                <Navigate to={role === "admin" ? "/dashboard" : "/home"} />
                            ) : (
                                <Login />
                            )
                        }
                    />

                    <Route path="/logout" element={<Logout />} />

                    <Route
                        path="/home"
                        element={
                            <PrivateRoute allowRoles={["user", "admin"]}>
                                <Home />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/admin/member-home/:memberId"
                        element={
                            <PrivateRoute allowRoles={["admin"]}>
                                <Home />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/members"
                        element={
                            <PrivateRoute allowRoles={["admin"]}>
                                <MemberPage />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/muscle"
                        element={
                            <PrivateRoute allowRoles={["admin"]}>
                                <MuscleGroups />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/muscle_exercises"
                        element={
                            <PrivateRoute allowRoles={["admin"]}>
                                <Exercises />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/programs"
                        element={
                            <PrivateRoute allowRoles={["admin"]}>
                                <Programs />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/members-programs"
                        element={
                            <PrivateRoute allowRoles={["admin"]}>
                                <MemberProgram />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/dashboard"
                        element={
                            <PrivateRoute allowRoles={["admin"]}>
                                <Dashboard />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/admin-chat"
                        element={
                            <PrivateRoute allowRoles={["admin"]}>
                                <AdminChat />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/profile"
                        element={
                            <PrivateRoute allowRoles={["user", "admin"]}>
                                <Profile />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="/change-password"
                        element={
                            <PrivateRoute allowRoles={["user", "admin"]}>
                                <ChangePassword />
                            </PrivateRoute>
                        }
                    />

                    <Route
                        path="*"
                        element={<Navigate to={token ? "/home" : "/login"} />}
                    />
                </Routes>
            </div>
        </div>
    );
});

export default App;
